import { Bolt, Boxes, ChevronsUpDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMatch, useNavigate, useParams } from 'react-router';

import { easeOutSmooth, Skeleton } from '@components/primitives';

import { UserObject } from '@api/types';
import { AppActions } from '@components/features/Admin/AppActions';
import { useTeam, useUser } from '@components/features/Admin/queries';
import { Search } from '@components/features/Admin/Search';
import { TeamActions } from '@components/features/Admin/TeamActions';
import { UserActions } from '@components/features/Admin/UserActions';
import { AppLockup } from '@components/features/Layout/Navigation/AppDropdown';
import { Separator } from '@components/features/Layout/Navigation/Separator';
import { SlantedButton } from '@components/features/Layout/Navigation/SlantedButton';
import css from '@components/features/Layout/Navigation/styles.module.css';
import { TeamLockup } from '@components/features/Layout/Navigation/TeamDropdown';
import { UserAvatar } from '@components/features/UserAvatar';
import { useAppQuery } from '@queries/app';

function UserLockup({ user }: { user: UserObject }) {
  return (
    <span className={css.userLockup}>
      <span className={css.avatar}>
        <UserAvatar uid={user.id} fallback={user.name} />
      </span>
      <span className={css.name}>
        {user.name ?? <Skeleton width={96} height={12} />}
      </span>
    </span>
  );
}

export function AdminBreadcrumbs() {
  const { teamUuid, appUuid } = useParams();
  const { data: team } = useTeam();
  const { data: app } = useAppQuery(teamUuid ?? '', appUuid ?? '', {
    enabled: !!teamUuid && !!appUuid,
  });
  const { data: user } = useUser();
  const isEnclaves = useMatch('/admin/enclaves');

  const navigate = useNavigate();

  return (
    <>
      <Separator key="admin-separator" />

      <div className={css.dropdown} key="admin-crumb">
        <SlantedButton onClick={() => navigate('/admin')}>
          <span className={css.adminIcon}>
            <Bolt />
          </span>
          <span>Admin</span>
        </SlantedButton>
      </div>

      {(team || user || isEnclaves) && (
        <Separator key="admin-crumb-separator" />
      )}

      <AnimatePresence initial={false} mode="popLayout">
        {team && (
          <motion.div
            key={`admin-team-${team.team.uuid}`}
            className={css.dropdown}
            data-no-shrink
            initial={{ opacity: 0, y: -10, x: 4, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 10, x: -4 }}
            transition={{
              type: 'spring',
              visualDuration: 0.3,
              bounce: 0.2,
              filter: { duration: 0.3, ease: easeOutSmooth },
            }}
          >
            <TeamActions team={team.team}>
              <SlantedButton suffix={<ChevronsUpDown />}>
                <TeamLockup teamUuid={team.team.uuid} />
              </SlantedButton>
            </TeamActions>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {app && team && <Separator key="app-separator" />}
        {app && team && (
          <motion.div
            key={app.uuid}
            className={css.dropdown}
            initial={{ opacity: 0, y: -10, x: 4, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 10, x: -4 }}
            transition={{
              type: 'spring',
              visualDuration: 0.3,
              bounce: 0.2,
              filter: { duration: 0.3, ease: easeOutSmooth },
            }}
          >
            <AppActions app={app}>
              <SlantedButton suffix={<ChevronsUpDown />}>
                <AppLockup teamUuid={team.team.uuid} appUuid={app.uuid} />
              </SlantedButton>
            </AppActions>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {user && (
          <motion.div
            key={`admin-user-${user.id}`}
            className={css.dropdown}
            initial={{ opacity: 0, y: -10, x: 4, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 10, x: -4 }}
            transition={{
              type: 'spring',
              visualDuration: 0.3,
              bounce: 0.2,
              filter: { duration: 0.3, ease: easeOutSmooth },
            }}
          >
            <UserActions user={user}>
              <SlantedButton suffix={<ChevronsUpDown />}>
                <UserLockup user={user} />
              </SlantedButton>
            </UserActions>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {isEnclaves && (
          <motion.div
            key={`admin-enclaves`}
            className={css.dropdown}
            data-no-shrink
            initial={{ opacity: 0, y: -10, x: 4, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 10, x: -4 }}
            transition={{
              type: 'spring',
              visualDuration: 0.3,
              bounce: 0.2,
              filter: { duration: 0.3, ease: easeOutSmooth },
            }}
          >
            <span className={css.enclavesCrumb}>
              <span className={css.icon}>
                <Boxes aria-hidden />
              </span>
              <span>Enclaves</span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function AdminLinks() {
  return (
    <>
      <div className={css.adminSearch}>
        <Search />
      </div>
    </>
  );
}
